

import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output, PLATFORM_ID, SimpleChanges, ViewChild, ViewContainerRef, ViewEncapsulation, afterNextRender, createNgModule, } from '@angular/core';
import { EnvironmentActions } from '@app/core/actions';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import * as fromRoot from '@app/reducers';
import { buildArrayFromRange } from '@app/shared/helpers/helpers';
import { select, Store } from '@ngrx/store';
import { MediaType, Options, Source, SourceInfo } from 'plyr';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { Constant } from 'src/constant';

const DEFAULT_SAMPLE_RATE = 1000;

@Component({
  selector: 'app-smash-player',
  templateUrl: './smash-player.component.html',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule],
  styleUrls: ['./smash-player.component.scss']
})
export class SmashPlayerComponent implements OnInit, OnDestroy {

  @Input() source: SourceInfo;
  @Input() options: Options;
  @Input() allowConcurrency = false;
  @Input() fullscreen = false;
  @Input() unstoppable = false;

  @Output() loading: EventEmitter<any> = new EventEmitter<any>();
  @Output() readyToPlay: EventEmitter<any> = new EventEmitter<any>();
  @Output() ended: EventEmitter<any> = new EventEmitter<any>();
  @Output() plyrEnterFullScreen: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() plyrExitFullScreen: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('_plyrRef', { static: true, read: ViewContainerRef }) _plyrRef!: ViewContainerRef;


  isPlyrLoaded: boolean = false;
  subscriptions: Subscription[] = [];

  player: Plyr;
  playerSources: Source[] = [];
  playerOptions: Options;
  playerType: string;
  playerProvider: 'vimeo' | 'youtube' | 'html5';
  playerShouldLoop = false;

  checkPlayerCompletionInterval: NodeJS.Timeout;
  completionPercentage = 0;
  completionsRates = buildArrayFromRange(0, 100, 5);
  completionRatesChecked = {};

  constructor(
    private readonly store$: Store<fromRoot.State>,
    private gtmService: GoogleTagManagerService,
    @Inject(PLATFORM_ID) private platformId: object,
    private injector: Injector,
  ) {
    afterNextRender(async () => {
      const { PlyrComponent, PlyrModule } = await import('ngx-plyr-mg');
      const moduleRef = createNgModule(PlyrModule, this.injector);
      let componentRef = this._plyrRef.createComponent(PlyrComponent, { ngModuleRef: moduleRef });
      const classList = ['player', `${this.playerProvider}-player`, `${this.fullscreen ? 'player-fullscreen' : 'player-normal'}`];
      classList.map(classValue => componentRef.location.nativeElement.classList.add(classValue));
      componentRef.setInput('plyrPlaysInline', true);
      componentRef.setInput('plyrType', this.playerType as MediaType);
      componentRef.setInput('plyrSources', this.playerSources);
      componentRef.setInput('plyrOptions', this.playerOptions);

      this.subscriptions.push(
        componentRef.instance.plyrInit.subscribe(($event) => this.initPlayer($event)),
        componentRef.instance.plyrLoadStart.subscribe(() => this.loadStart()),
        componentRef.instance.plyrCanPlay.subscribe(() => this.canPlay()),
        componentRef.instance.plyrEnded.subscribe(() => this.onEnd()),
        componentRef.instance.plyrEnterFullScreen.subscribe(() => this.onPlyrEnterFullScreen()),
        componentRef.instance.plyrExitFullScreen.subscribe(() => this.onPlyrExitFullScreen()),
      )
    })
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.playerSources = this.source.sources;
      this.playerProvider = this.source.sources[0].provider; // FIX ME
      this.playerOptions = { ...this.options };
      this.playerType = this.source.type;
      if (this.playerProvider === 'youtube' && this.playerOptions?.loop?.active === true) {
        this.playerOptions.loop.active = false;
        this.playerShouldLoop = true;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    /* This is a way to change source from player if the source has the same provider (vimeo/youtube) as the previous source */
    if (!!changes?.source?.currentValue) {
      this.playerSources = this.source.sources;
      this.playerType = this.source.type;
    }
    if (!!changes?.options?.currentValue) {
      this.playerOptions = this.options;
    }
  }

  initPlayer(event) {
    if (isPlatformBrowser(this.platformId)) {
      this.player = event;
      this.registerPlayer();
      this.listenToEvents();
      this.mutePlayerIfNeeded();
    }
  }

  registerPlayer(): void {
    this.store$.dispatch(EnvironmentActions.AddPlayer({ id: btoa(this.source.sources[0].src), url: this.source.sources[0].src, allowConcurrency: this.allowConcurrency, unstoppable: this.unstoppable }));
  }

  pushGtmCompletionEvent(completionRate: number): void {
    this.gtmService.pushTag({
      event: Constant.eventGTM.VIEW_VIDEO_COMPLETION,
      video_completion: completionRate,
      video_duration: this.player.duration,
      video_current_time: this.player.currentTime,
      video_id: btoa(this.source.sources[0].src),
      video_provider: this.source.sources[0].provider,
      video_title: (this.player as any)?.config ? (this.player as any).config.title : '',
    });
  }

  listenToEvents() {
    this.player.on('ready', (event) => {
      if (this.options.autoplay) {
        // FIX ME 29072021 Update on Youtube player ? Autoplay doesn't seem to work well...
        this.player.play();
      }
    });

    // FIX ME workaround for loop on Youtube video that makes froze .5s before playing again...
    this.player.on('ended', (event) => {
      if (this.playerProvider === 'youtube' && this.playerShouldLoop) {
        this.player.play();
      }
      if (!this.completionRatesChecked[100]) {
        this.completionRatesChecked[100] = true;
        this.pushGtmCompletionEvent(100);
        this.endCheckCompletion();
      }
    });

    this.player.on('playing', (event) => {
      this.startCheckCompletion();
      this.store$.dispatch(EnvironmentActions.UpdatePlayerPlayingAndStopOthers({ id: btoa(this.source.sources[0].src) }));
    });
    this.subscriptions.push(
      this.store$.pipe(select(fromRoot.getPlayer(btoa(this.source.sources[0].src)))).pipe(distinctUntilChanged()).subscribe(res => {
        if (res?.playing === false) {
          this.pause();
        }
      })
    );
  }

  mutePlayerIfNeeded(): void {
    if (this.player && this.playerProvider === 'youtube' || this.playerProvider === 'vimeo') {
      this.player.muted = true;
    }
  }

  play() {
    this.player.play();
  }

  pause() {
    this.player.pause();
  }

  loadStart() {
    this.loading.emit(true);
  }

  canPlay() {
    this.readyToPlay.emit(true);
  }

  onEnd() {
    this.ended.emit(true);
  }

  onPlyrEnterFullScreen(): void {
    this.plyrEnterFullScreen.emit(true);
  }

  onPlyrExitFullScreen(): void {
    this.plyrExitFullScreen.emit(true);
  }

  startCheckCompletion() {
    this.checkPlayerCompletionInterval = setInterval(() => {
      this.checkPlayerCompletion();
    }, DEFAULT_SAMPLE_RATE);
  }

  endCheckCompletion() {
    clearInterval(this.checkPlayerCompletionInterval);
  }

  checkPlayerCompletion() {
    if (!this.player || !this.player.duration || !this.player.currentTime) {
      return;
    }
    const duration = this.player.duration;
    const percentage = (this.player.currentTime / duration) * 100;
    this.completionPercentage = Math.round((percentage + Number.EPSILON) * 100) / 100;
    this.completionsRates.forEach((rate, index) => {
      if (this.completionPercentage >= rate && !this.completionRatesChecked[rate]) {
        this.completionRatesChecked[rate] = true;
        this.pushGtmCompletionEvent(rate);
      }
    });
  }

  get isPaused(): boolean {
    return this.player?.paused;
  }

  get isPlaying(): boolean {
    return this.player?.playing;
  }

  ngOnDestroy(): void {
    this.player.destroy();
    this.endCheckCompletion();
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }

}
